import { LenderMap, LenderName, PaymentMethodType, Pricing, Program } from '@nemuru-eng/js-api-client';
import { InsuranceType } from '@nemuru-eng/js-api-client/slim';

import { getLendersFromPricing } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/utils/getLendersFromPricing';
import { LenderEarningFee } from '@/components/modules/PricingEditor/types/LenderEarningFee';
import { InclusionFilter } from '@/components/modules/PricingsList/_shared/constants/InclusionFilterSelectOptions';
import { ALL_PAYMENT_METHODS } from '@/components/modules/PricingsList/_shared/schemas/pricingsForm.schema';
import { UserEntity } from '@/entities/UserEntity';
import UserInMerchantStructure from '@/entities/UserInMerchantStructure';

export class PricingsListEntity {
  private static instance: PricingsListEntity;
  private filters: Partial<{
    paymentMethod: PaymentMethodType | typeof ALL_PAYMENT_METHODS;
    gracePeriod: InclusionFilter;
    setupFee: InclusionFilter;
    insurances: InsuranceType[];
    programs: Program[];
    lender: LenderName;
    search: string;
  }> = {};
  private filteredPricings: Pricing[] = [];
  private pricingsByPaymentMethod?: Pricing[] = [];
  private originalPricings?: Pricing[];
  private availableInsurancesByPricings?: InsuranceType[] = [];
  private programsByPricings?: Program[] = [];

  private constructor() {}

  public static getInstance(): PricingsListEntity {
    return (this.instance ??= new PricingsListEntity());
  }

  public getFilters() {
    return this.filters;
  }

  public getFilteredPricings(): Pricing[] {
    return this.filteredPricings;
  }

  public getOriginalPricings() {
    return this.originalPricings;
  }

  public setPricings(pricings: Pricing[], userEntity: UserEntity | UserInMerchantStructure): this {
    if (userEntity.isRoleAdminOrSuperAdmin()) {
      this.originalPricings = pricings;
    } else {
      this.originalPricings = this.getPricingsByNameInLenderContext(pricings);
    }
    this.applyFilters();
    return this;
  }

  public resetPricings(): void {
    this.originalPricings = undefined;
    this.filteredPricings = [];
  }

  public setFilters(filters: Partial<typeof this.filters>): this {
    Object.assign(this.filters, filters);
    this.applyFilters();
    return this;
  }

  public getPricingsByPaymentMethodFilter(): Pricing[] {
    if (!this.originalPricings) return [];

    if (!this.filters.paymentMethod) return this.originalPricings;

    this.pricingsByPaymentMethod = this.filterByPaymentMethod(this.originalPricings, this.filters.paymentMethod);
    return this.pricingsByPaymentMethod;
  }

  public getAvailableInsurancesByPricings() {
    this.setAvailableInsurancesInPricings();
    return this.availableInsurancesByPricings;
  }

  private setAvailableInsurancesInPricings(): void {
    if (!this.pricingsByPaymentMethod) return;

    const availableInsurancesByPricings = [
      ...new Set(
        this.pricingsByPaymentMethod.flatMap((pricing) =>
          pricing.ranges[0].availableInsurances.map((insurance) => insurance.type),
        ),
      ),
    ];

    this.availableInsurancesByPricings = Array.from(availableInsurancesByPricings) as InsuranceType[];
  }

  public getProgramsByPricings() {
    this.setProgramsInPricings();
    return this.programsByPricings;
  }

  public getPricingsByNameInLenderContext(pricings: Pricing[]): Pricing[] {
    const pricingsByNameInLenderContext = pricings.reduce((acc, pricing) => {
      if (!this.hasLenderEarningsFees(pricing)) {
        return this.handlePricingWithoutLenderFees(acc, pricing);
      }

      return this.handlePricingWithLenderFees(acc, pricing);
    }, [] as Pricing[]);

    return this.filterPricingsFromNemuru(pricingsByNameInLenderContext);
  }

  private setProgramsInPricings(): void {
    if (!this.pricingsByPaymentMethod) return;

    const ProgramsByPricings = [...new Set(this.pricingsByPaymentMethod.flatMap((pricing) => pricing.program))];

    this.programsByPricings = Array.from(ProgramsByPricings) as Program[];
  }

  private applyFilters(): void {
    if (!this.originalPricings) return;
    let filteredPricings = this.originalPricings;

    if (this.filters.paymentMethod) {
      filteredPricings = this.filterByPaymentMethod(filteredPricings, this.filters.paymentMethod);
    }
    if (this.filters.gracePeriod !== undefined) {
      filteredPricings = this.filterByGracePeriod(filteredPricings, this.filters.gracePeriod);
    }
    if (this.filters.lender) {
      filteredPricings = this.filterByLender(filteredPricings, this.filters.lender);
    }
    if (this.filters.setupFee !== undefined) {
      filteredPricings = this.filterBySetupFee(filteredPricings, this.filters.setupFee);
    }
    if (this.filters.insurances && this.filters.insurances.length > 0) {
      filteredPricings = this.filterByInsurance(filteredPricings, this.filters.insurances);
    }
    if (this.filters.programs && this.filters.programs.length > 0) {
      filteredPricings = this.filterByProgram(filteredPricings, this.filters.programs);
    }
    if (this.filters.search) {
      filteredPricings = this.filterBySearch(filteredPricings, this.filters.search);
    }
    this.filteredPricings = filteredPricings;
  }

  private filterByInsurance(pricings: Pricing[], insurances: InsuranceType[]) {
    return pricings.filter((pricing) =>
      pricing.ranges.every((range) =>
        insurances.some((insurance) =>
          range.availableInsurances.map((insurance) => insurance.type).includes(insurance),
        ),
      ),
    );
  }

  private filterByProgram(pricings: Pricing[], programs: Program[]) {
    return pricings.filter((pricing) => programs.includes(pricing.program));
  }

  private filterBySetupFee(pricings: Pricing[], setupFee: InclusionFilter | undefined): Pricing[] {
    if (!setupFee || setupFee === InclusionFilter.ALL) return pricings;

    if (setupFee === InclusionFilter.WITH)
      return pricings.filter((pricing) => pricing.ranges.every((range) => range.setupFee.value > 0));

    return pricings.filter((pricing) => pricing.ranges.every((range) => range.setupFee.value === 0));
  }

  private filterByLender(pricings: Pricing[], lender: LenderName): Pricing[] {
    return pricings.filter((pricing) => this.getLenders(pricing).includes(lender));
  }

  private filterBySearch(pricings: Pricing[], search: string): Pricing[] {
    return pricings.filter((pricing) => {
      const pricingNamesInLenderContext = pricing.ranges
        .flatMap((range) => range.lenderEarningsFees || [])
        .map((fee) => fee.pricingNameInLenderContext)
        .reduce((acc, name) => {
          if (name && !acc.includes(name.toLowerCase())) {
            acc.push(name.toLowerCase());
          }
          return acc;
        }, [] as string[]);

      return (
        pricing.name.toLowerCase().includes(search.toLowerCase()) ||
        pricingNamesInLenderContext.some((name) => name.includes(search.toLowerCase()))
      );
    });
  }

  private filterByGracePeriod(pricings: Pricing[], filter: InclusionFilter | undefined): Pricing[] {
    if (!filter || filter === InclusionFilter.ALL) return pricings;

    if (filter === InclusionFilter.WITH)
      return pricings.filter((pricing) => pricing.ranges.every((range) => range.gracePeriod));
    if (filter === InclusionFilter.WITHOUT)
      return pricings.filter((pricing) => !pricing.ranges.every((range) => range.gracePeriod));

    return pricings;
  }

  private filterByPaymentMethod(
    pricings: Pricing[],
    paymentMethod: PaymentMethodType | typeof ALL_PAYMENT_METHODS,
  ): Pricing[] {
    return paymentMethod === ALL_PAYMENT_METHODS
      ? pricings
      : pricings.filter((pricing) => pricing.paymentMethodTypes.includes(paymentMethod));
  }

  private hasLenderEarningsFees(pricing: Pricing): boolean {
    return pricing.ranges.some((range) => range.lenderEarningsFees && range.lenderEarningsFees.length > 0);
  }

  private handlePricingWithoutLenderFees(acc: Pricing[], pricing: Pricing): Pricing[] {
    const existingPricing = acc.find((pricingInAcc) => pricingInAcc.name === pricing.name);

    if (existingPricing) {
      existingPricing.ranges.push(...pricing.ranges);
    } else {
      acc.push(pricing);
    }

    return acc;
  }

  private handlePricingWithLenderFees(acc: Pricing[], pricing: Pricing): Pricing[] {
    pricing.ranges.forEach((range) => {
      if (!range.lenderEarningsFees) return;

      range.lenderEarningsFees.forEach((fee) => {
        const existingPricing = this.findExistingPricing(acc, fee.pricingNameInLenderContext, fee.lenderId);

        if (existingPricing) {
          this.addRangeToExistingPricing(existingPricing, range, fee);
        } else {
          this.createNewPricingFromLenderFee(acc, pricing, range, fee);
        }
      });
    });

    return acc;
  }

  private filterPricingsFromNemuru(pricings: Pricing[]): Pricing[] {
    const filteredPricings: Pricing[] = [];

    pricings.forEach((pricing) => {
      if (
        pricing.ranges.some((range) =>
          range.lenderEarningsFees?.some((l) => l.lenderId === LenderMap.fromLenderName(LenderName.NEMURU)),
        )
      ) {
        // Do not include pricing from Nemuru
        return;
      }
      filteredPricings.push(pricing);
    });

    return filteredPricings;
  }

  private findExistingPricing(acc: Pricing[], name: string, lenderId: string): Pricing | undefined {
    return acc.find(
      (pricingInAcc) =>
        pricingInAcc.name === name && pricingInAcc.ranges?.[0]?.lenderEarningsFees?.[0]?.lenderId === lenderId,
    );
  }

  private addRangeToExistingPricing(
    existingPricing: Pricing,
    range: Pricing['ranges'][number],
    fee: LenderEarningFee,
  ): void {
    existingPricing.ranges.push({
      ...range,
      lenderEarningsFees: [fee],
    });
  }

  private createNewPricingFromLenderFee(
    acc: Pricing[],
    originalPricing: Pricing,
    range: Pricing['ranges'][number],
    fee: LenderEarningFee,
  ): void {
    const newPricing: Pricing = {
      ...originalPricing,
      name: fee.pricingNameInLenderContext,
      ranges: [
        {
          ...range,
          lenderEarningsFees: [fee],
        },
      ],
    };

    acc.push(newPricing);
  }

  public getLenders(pricing: Pricing): LenderName[] {
    return getLendersFromPricing(pricing);
  }
}
